import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { haveSameProviders, registeredTriggers, selectProviders, } from '#pplx/frontend/composer-sdk/internal/typeahead/arbitration.js';
import { detectActiveTrigger } from '#pplx/frontend/composer-sdk/internal/typeahead/detectActiveTrigger.js';
import { makeEditorApi } from '#pplx/frontend/composer-sdk/internal/typeahead/editorApi.js';
import { TypeaheadResultsEngine } from '#pplx/frontend/composer-sdk/internal/typeahead/TypeaheadResultsEngine.js';
import { useDispatchTypeaheadSelection, useTypeaheadListNavigation, } from '#pplx/frontend/composer-sdk/internal/typeahead/useTypeaheadListNavigation.js';
import { BLUR_COMMAND, COMMAND_PRIORITY_LOW, FOCUS_COMMAND, KEY_ESCAPE_COMMAND, $getSelection, $isRangeSelection, $getRoot, } from 'lexical';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
/**
 * Owns the input-anchored path used by empty-query suggestions and persistent generic palettes.
 * `LexicalTypeaheadMenuPlugin` cannot open on an empty editor and closes when the selection is
 * noncollapsed. A persistent palette therefore stays entirely on this path, deriving its query
 * from the editor state and keeping one overlay mounted across focus and selection changes.
 */
export function useInputAnchoredTypeahead({ editor, providers, explicitProviders, resolveTypedTriggerProviders, engine, state, allowEmptyGeneric, keepOpen, wrapNavigation, suspended, activePrefixRef, selectedProvidersRef, explicitQueryRef, setActivePrefix, close, }) {
    const [active, setActive] = useState(false);
    const activeRef = useRef(false);
    const lastQueryRef = useRef(null);
    const focusedRef = useRef(false);
    const editorEmptyRef = useRef(true);
    // Set when the user dismisses the overlay with Escape; keeps `syncEmptyPath` from reopening it
    // while still focused + empty. Cleared on blur or typing so the next open behaves normally,
    // matching Lexical's per-open lifecycle.
    const dismissedRef = useRef(false);
    const { selectedIndex, rowsRef, selectRowRef, onHighlight, onArrowKey, resetSelection } = useTypeaheadListNavigation({
        editor,
        enabled: true,
        activeRef,
        state,
        initialSelection: selectedProvidersRef.current[0]?.initialSelection,
        wrapNavigation,
        navigation: selectedProvidersRef.current[0]?.navigation,
    });
    // A live result refresh must not change the open callback's identity and reopen the path.
    const retainHighlightRef = useRef(false);
    const selectedProviderIndex = state.status === 'done' || state.status === 'loading'
        ? state.rows[selectedIndex ?? -1]?.providerIndex
        : undefined;
    retainHighlightRef.current =
        'providerSnapshots' in state &&
            state.providerSnapshots?.some((snapshot) => snapshot.highlightKey !== undefined && snapshot.providerIndex === selectedProviderIndex) === true;
    const openInputAnchoredPath = useCallback((query, selected) => {
        selectedProvidersRef.current = selected;
        activePrefixRef.current = '';
        setActivePrefix('');
        if (!activeRef.current || lastQueryRef.current !== query || !retainHighlightRef.current) {
            resetSelection();
        }
        lastQueryRef.current = query;
        activeRef.current = true;
        setActive(true);
        engine.run(query, selected);
    }, [engine, activePrefixRef, selectedProvidersRef, setActivePrefix, resetSelection]);
    // Open the empty-query path: fetch the generic providers' default suggestions and mark the
    // parallel overlay active. The `''` prefix mirrors what the normal path sets for a generic
    // match, so `displayedVariant`/`activeMaxVisibleResults` resolve the same way.
    const openEmptyPath = useCallback(() => {
        const generic = selectProviders(providers, '').filter((provider) => provider.activateOnEmptyQuery);
        if (generic.length === 0) {
            return;
        }
        openInputAnchoredPath('', generic);
    }, [providers, openInputAnchoredPath]);
    const deactivateInputAnchoredPath = useCallback(() => {
        activeRef.current = false;
        lastQueryRef.current = null;
        setActive(false);
        resetSelection();
    }, [resetSelection]);
    // Reconcile the empty path with focus + emptiness. When leaving it because the user typed,
    // the normal (Lexical-driven) path takes over the shared engine/state, so only tear that
    // down when nothing else will drive it (e.g. blur) to avoid clobbering the typed results.
    const syncEmptyPath = useCallback(() => {
        const shouldOpen = !suspended &&
            focusedRef.current &&
            !dismissedRef.current &&
            (explicitQueryRef.current !== null || (allowEmptyGeneric && editorEmptyRef.current));
        if (shouldOpen === activeRef.current) {
            return;
        }
        if (shouldOpen) {
            openEmptyPath();
            return;
        }
        deactivateInputAnchoredPath();
        const lexicalWillDrive = focusedRef.current && !editorEmptyRef.current;
        if (!lexicalWillDrive) {
            close();
        }
    }, [
        allowEmptyGeneric,
        suspended,
        explicitQueryRef,
        openEmptyPath,
        deactivateInputAnchoredPath,
        close,
    ]);
    useEffect(() => {
        const explicitQuery = explicitQueryRef.current;
        if (explicitQuery !== null) {
            const selected = providers.filter((provider) => selectedProvidersRef.current.some((previous) => (previous.registration?.id ?? previous) === (provider.registration?.id ?? provider)) && acceptsExplicitSuggestions(provider, explicitQuery));
            if (suspended || selected.length === 0) {
                explicitQueryRef.current = null;
                deactivateInputAnchoredPath();
                close();
            }
            else if (!haveSameProviders(selectedProvidersRef.current, selected)) {
                openInputAnchoredPath(explicitQuery, selected);
            }
        }
    }, [
        close,
        deactivateInputAnchoredPath,
        explicitQueryRef,
        openInputAnchoredPath,
        providers,
        selectedProvidersRef,
        suspended,
    ]);
    const openSuggestions = useCallback(({ pluginId, expectedQuery }) => {
        // The editor's DOM focus is authoritative, including before passive focus listeners mount.
        if (keepOpen || suspended || document.activeElement !== editor.getRootElement())
            return false;
        const query = editor.getEditorState().read(() => $getRoot().getTextContent());
        if (query !== expectedQuery)
            return false;
        const selected = providers.filter((provider) => provider.registration?.pluginId === pluginId &&
            acceptsExplicitSuggestions(provider, query));
        if (selected.length === 0)
            return false;
        dismissedRef.current = false;
        explicitQueryRef.current = query;
        openInputAnchoredPath(query, selected);
        // Retire the caret menu without remounting it, so Escape can keep these rows dismissed.
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection))
                selection.dirty = true;
        });
        return true;
    }, [editor, explicitQueryRef, keepOpen, openInputAnchoredPath, providers, suspended]);
    const selectRow = useDispatchTypeaheadSelection({
        editor,
        onSelection: (index, selectionMethod) => {
            const row = rowsRef.current[index];
            const provider = row ? selectedProvidersRef.current[row.providerIndex] : undefined;
            if (!row || !provider || row.item.disabled === true)
                return null;
            // Empty-query selections have no trigger node; providers should activate directly
            // instead of using trigger-mutation helpers that need a trigger span.
            return {
                outcome: provider.onSelect(row.item, makeEditorApi(editor, null), Object.freeze({ selectionMethod, flatIndex: index })),
            };
        },
        onClose: () => {
            if (explicitQueryRef.current === null)
                return;
            explicitQueryRef.current = null;
            dismissedRef.current = true;
            deactivateInputAnchoredPath();
            close();
        },
        onHandoff: () => {
            explicitQueryRef.current = null;
            dismissedRef.current = true;
            deactivateInputAnchoredPath();
        },
    });
    selectRowRef.current = selectRow;
    const selectPointerRow = useCallback((index) => selectRow(index, 'pointer'), [selectRow]);
    usePersistentTypeahead({
        enabled: keepOpen,
        editor,
        providers,
        explicitProviders,
        resolveTypedTriggerProviders,
        suspended,
        onOpen: openInputAnchoredPath,
        onDeactivate: deactivateInputAnchoredPath,
    });
    // The normal empty-query lifecycle is inactive while the persistent lifecycle owns the path.
    useEffect(() => {
        if (keepOpen) {
            return;
        }
        focusedRef.current = document.activeElement === editor.getRootElement();
        const initialQuery = editor.getEditorState().read(() => $getRoot().getTextContent());
        editorEmptyRef.current = initialQuery.length === 0;
        syncEmptyPath();
        const removeFocus = editor.registerCommand(FOCUS_COMMAND, () => {
            focusedRef.current = true;
            syncEmptyPath();
            return false;
        }, COMMAND_PRIORITY_LOW);
        const removeBlur = editor.registerCommand(BLUR_COMMAND, () => {
            focusedRef.current = false;
            explicitQueryRef.current = null;
            // Clear any Escape dismissal so refocusing the empty editor opens the overlay again.
            dismissedRef.current = false;
            syncEmptyPath();
            return false;
        }, COMMAND_PRIORITY_LOW);
        const removeUpdate = editor.registerUpdateListener(({ editorState }) => {
            const query = editorState.read(() => $getRoot().getTextContent());
            if (explicitQueryRef.current !== null && explicitQueryRef.current !== query) {
                explicitQueryRef.current = null;
            }
            editorEmptyRef.current = query.length === 0;
            // Typing clears an Escape dismissal so clearing back to empty reopens the overlay.
            if (query.length > 0) {
                dismissedRef.current = false;
            }
            syncEmptyPath();
        });
        return () => {
            removeFocus();
            removeBlur();
            removeUpdate();
        };
    }, [editor, explicitQueryRef, keepOpen, syncEmptyPath]);
    // Escape dismisses the overlay; `dismissedRef` keeps `syncEmptyPath` from reopening it while
    // the editor stays focused + empty (blur or typing clears the dismissal). The row navigation
    // keys live in `useTypeaheadListNavigation` (the normal path's keys come from `LexicalMenu`,
    // which isn't mounted here).
    useEffect(() => {
        const removeEscape = editor.registerCommand(KEY_ESCAPE_COMMAND, (event) => {
            if (!activeRef.current) {
                return false;
            }
            // Persistent palettes own Escape at the surface level (usually to close the modal).
            if (keepOpen) {
                return false;
            }
            // Suppress the browser's native Escape handling (e.g. cancelling an in-progress IME
            // composition) so the keypress only dismisses the overlay, matching Lexical's handler.
            event?.preventDefault();
            const wasExplicit = explicitQueryRef.current !== null;
            explicitQueryRef.current = null;
            dismissedRef.current = true;
            syncEmptyPath();
            if (wasExplicit)
                close();
            return true;
        }, COMMAND_PRIORITY_LOW);
        return removeEscape;
    }, [editor, explicitQueryRef, keepOpen, syncEmptyPath, close]);
    return {
        active,
        selectedIndex,
        onHighlight,
        onArrowKey,
        onSelect: selectPointerRow,
        openSuggestions,
    };
}
/** A live refresh must not retain a provider that could no longer accept an explicit opening. */
function acceptsExplicitSuggestions(provider, query) {
    if (provider.trigger !== '' ||
        (provider.matchQuery !== undefined && !provider.matchQuery(query))) {
        return false;
    }
    switch (provider.display) {
        case 'suggestDropdown':
        case 'spotlight':
            return true;
        case 'inline':
        case 'commandMenu':
        case undefined:
            return false;
        default:
            return assertUnreachableValue(provider.display);
    }
}
/**
 * Owns the persistent generic query lifecycle. It yields to an explicit Lexical trigger while the
 * editor is focused, then restores the generic menu when the explicit trigger ends or focus leaves.
 */
function usePersistentTypeahead({ enabled, editor, providers, explicitProviders, resolveTypedTriggerProviders, suspended, onOpen, onDeactivate, }) {
    const activeRef = useRef(false);
    const focusedRef = useRef(false);
    const queryRef = useRef('');
    const providersRef = useRef(providers);
    const explicitTriggers = useMemo(() => registeredTriggers(explicitProviders), [explicitProviders]);
    const sync = useCallback((query) => {
        const providersChanged = !haveSameProviders(providersRef.current, providers);
        providersRef.current = providers;
        // Yield while a focused explicit trigger or the marker-anchored command
        // menu owns the overlay; restore the generic menu when it ends. A typed
        // trigger owns the overlay only when its veto and query checks accept it,
        // so a rejected trigger leaves the generic menu open.
        const detected = focusedRef.current ? detectActiveTrigger(query, explicitTriggers) : null;
        const yieldsToOtherPath = suspended || (detected !== null && resolveTypedTriggerProviders(detected).length > 0);
        if (yieldsToOtherPath) {
            queryRef.current = query;
            if (activeRef.current) {
                activeRef.current = false;
                onDeactivate();
            }
            return;
        }
        if (activeRef.current && query === queryRef.current && !providersChanged) {
            return;
        }
        const selected = selectProviders(providers, '');
        queryRef.current = query;
        if (selected.length === 0) {
            if (activeRef.current) {
                activeRef.current = false;
                onDeactivate();
            }
            return;
        }
        activeRef.current = true;
        onOpen(query, selected);
    }, [explicitTriggers, resolveTypedTriggerProviders, suspended, onDeactivate, onOpen, providers]);
    // Open before paint so an auto-focused persistent surface enters as one attached card.
    useLayoutEffect(() => {
        if (!enabled) {
            if (activeRef.current) {
                activeRef.current = false;
                onDeactivate();
            }
            return;
        }
        focusedRef.current = document.activeElement === editor.getRootElement();
        const query = editor.getEditorState().read(() => $getRoot().getTextContent());
        sync(query);
    }, [editor, enabled, onDeactivate, sync]);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        focusedRef.current = document.activeElement === editor.getRootElement();
        const removeFocus = editor.registerCommand(FOCUS_COMMAND, () => {
            focusedRef.current = true;
            const query = editor.getEditorState().read(() => $getRoot().getTextContent());
            sync(query);
            return false;
        }, COMMAND_PRIORITY_LOW);
        const removeBlur = editor.registerCommand(BLUR_COMMAND, () => {
            focusedRef.current = false;
            const query = editor.getEditorState().read(() => $getRoot().getTextContent());
            sync(query);
            return false;
        }, COMMAND_PRIORITY_LOW);
        const removeUpdate = editor.registerUpdateListener(({ editorState }) => {
            const query = editorState.read(() => $getRoot().getTextContent());
            sync(query);
        });
        return () => {
            removeFocus();
            removeBlur();
            removeUpdate();
        };
    }, [editor, enabled, sync]);
}
//# sourceMappingURL=useInputAnchoredTypeahead.js.map